import React, { useEffect, useRef, useState } from 'react';
import { graphql } from 'gatsby';
import Layout from '../../layout/Layout';
import { SEO } from '../../layout/SEO';
import { processMetatags } from '../../shared/metaTags';
import { makeAnalyticsCall } from '../../shared/analytics/callAnalytics';
import 'mapbox-gl/src/css/mapbox-gl.css';
// eslint-disable-next-line
// @ts-ignore
import mapboxgl from '!mapbox-gl'; //https://docs.mapbox.com/mapbox-gl-js/api/#excluding-gl-js-explicitly-from-transpilation
import { MARKET_ROUTING } from './market-routing';
import { AccentedText } from '../../components/AccentedText/AccentedText';
import { KiteIcon, KiteSelect } from '@kite/react-kite';
import { EventName } from '../../models/GTMEventTypes';
import './MarketingMap.scss';

mapboxgl.accessToken = `pk.eyJ1IjoibWVkaWFzb2x1dGlvbnMiLCJhIjoiY2owNDY5bTF2MGVpNzJxcDNoM2tvMXRoOCJ9.huElzhuVhxP52X6okUoB-w`;
const MAPBOX_STYLE = `mapbox://styles/mediasolutions/cj1a5z3td003e2rpauzcq162n`;
const MIN_ZOOM = 4;
const MAX_ZOOM = 12;
const DEFAULT_CENTER = [-97.0364, 38.8951];
const DEFAULT_ZOOM = 4;

interface Market {
    id: string;
    name: string;
    url: string;
}

export default function MarketingMap({ data }) {
    const { nodeMarketingMap: page } = data;
    const { json: marketsJson } = data;
    const map = useRef<any>(null);
    const popup = useRef<any>(null);
    const hoveredMarket = useRef<{
        type: string;
        geometry: any;
        properties: Market;
    }>(null);
    const [selectedMarket, setSelectedMarket] = useState<Market>(null);
    const [marketGeometry, setMarketGeometry] = useState<any>(null);

    const handleMouseMove = (e) => {
        const features = map.current.queryRenderedFeatures(e.point);
        const polyFeatures = features
            ? features.filter((f) => f.layer?.id === `markets`)
            : [];
        if (!polyFeatures.length) {
            map.current.getCanvas().style.cursor = ``;
            if (popup.current) popup.current.remove();
            hoveredMarket.current = null;
        } else {
            const [feature] = polyFeatures;
            hoveredMarket.current = feature;
            map.current.getCanvas().style.cursor = `pointer`;
            if (popup.current)
                popup.current
                    .setLngLat(e.lngLat)
                    .setHTML(
                        `<h3 class="popup-title">${feature.properties?.name}</h3>`
                    )
                    .addTo(map.current);
        }
    };

    const handleMapClick = () => {
        if (hoveredMarket.current) {
            window.location.assign(hoveredMarket.current.properties.url);
        }
    };

    const zoom = (increment: number) => {
        if (increment > 0) {
            map.current.zoomIn();
        } else {
            map.current.zoomOut();
        }
    };

    const reset = () => {
        map.current.flyTo({
            center: DEFAULT_CENTER,
            zoom: DEFAULT_ZOOM,
        });
    };

    const handleMarketSelect = (evt) => {
        setSelectedMarket(
            MARKET_ROUTING.find((m) => m.id === evt.target.value)
        );
        const matchedGeom = marketGeometry.features.find(
            (feat) => feat.properties.id === evt.target.value
        );
        if (!matchedGeom) return;

        const { geometry } = matchedGeom;
        const coordinates = geometry.coordinates[0];
        let bounds = null;
        if (geometry.type === `MultiPolygon`) {
            const bbox = new mapboxgl.LngLatBounds(
                coordinates[0][0],
                coordinates[0][0]
            );
            coordinates.forEach((ring) => {
                bounds = ring.reduce((b, coord) => b.extend(coord), bbox);
            });
        } else {
            bounds = coordinates.reduce(
                (b, coord) => b.extend(coord),
                new mapboxgl.LngLatBounds(coordinates[0], coordinates[0])
            );
        }
        map.current.fitBounds(bounds, {
            padding: 20,
            linear: true,
        });

        document.getElementById(`map`).scrollIntoView({
            behavior: `smooth`,
            block: `end`,
            inline: `nearest`,
        });

        map.current.setLayoutProperty(`markets`, `visibility`, `none`);
        map.current.addSource(`market`, {
            data: matchedGeom,
            type: `geojson`,
        });

        map.current.addLayer({
            id: `market`,
            type: `fill`,
            source: `market`,
            paint: {
                'fill-color': `#009bdb`,
                'fill-opacity': 0.3,
            },
        });

        setTimeout(() => {
            window.location.assign(matchedGeom.properties.url);
        }, 2000);
    };

    useEffect(() => {
        makeAnalyticsCall(EventName.PAGE_VIEW, {
            pageType: `Marketing Map`,
        });

        if (map.current) return;

        map.current = new mapboxgl.Map({
            container: `map`,
            center: DEFAULT_CENTER,
            zoom: DEFAULT_ZOOM,
            minZoom: MIN_ZOOM,
            maxZoom: MAX_ZOOM,
            scrollZoom: false,
            dragPan: false,
            doubleClickZoom: false,
            touchZoomRotate: false,
            style: MAPBOX_STYLE,
        });

        popup.current = new mapboxgl.Popup({
            closeButton: false,
            closeOnClick: false,
        });

        map.current.on(`load`, () => {
            fetch(marketsJson.publicURL)
                .then((response) => response.json())
                .then((data) => {
                    for (const feature of data.features) {
                        const match = MARKET_ROUTING.find(
                            (m) => m.id === feature.properties.id
                        );
                        if (match) {
                            feature.properties = match;
                        }
                    }

                    setMarketGeometry(data);

                    map.current.addSource(`markets`, {
                        data: data,
                        type: `geojson`,
                    });

                    map.current.addLayer({
                        id: `markets`,
                        type: `fill`,
                        source: `markets`,
                        paint: {
                            'fill-color': `#009bdb`,
                            'fill-opacity': 0.3,
                        },
                    });

                    map.current.on(`mousemove`, handleMouseMove);
                    map.current.on(`click`, handleMapClick);
                });
        });
    }, []);

    return (
        <Layout>
            <div className="p--map">
                <header className="kite-container">
                    <div>
                        <h1>
                            <AccentedText>{page.field_heading}</AccentedText>
                        </h1>
                        <div
                            dangerouslySetInnerHTML={{
                                __html: page.body?.value,
                            }}
                        ></div>
                    </div>
                    <div>
                        <KiteSelect
                            onChange={handleMarketSelect}
                            id="selected-market"
                            name="selected-market"
                            value={selectedMarket?.id ?? ``}
                            label="Where do you want to advertise?"
                        >
                            <option value="">Select...</option>
                            {MARKET_ROUTING.sort((a, b) =>
                                a.name.localeCompare(b.name)
                            ).map((m, idx) => (
                                <option key={idx} value={m.id}>
                                    {m.name}
                                </option>
                            ))}
                        </KiteSelect>
                    </div>
                </header>
                <div
                    className="map"
                    id="map"
                    onMouseMove={() =>
                        map.current && map.current.dragPan.enable()
                    }
                >
                    <div className="controls">
                        <button onClick={() => zoom(1)}>
                            <KiteIcon icon="ki-plus" />
                        </button>
                        <button onClick={() => zoom(-1)}>
                            <KiteIcon icon="ki-minus" />
                        </button>
                        <button onClick={() => reset()}>
                            <KiteIcon icon="ki-refresh" />
                        </button>
                    </div>
                </div>
            </div>
        </Layout>
    );
}

export const Head = ({ data }) => {
    const { nodeMarketingMap: page } = data;
    return (
        <>
            <SEO location={page.fields.url}>
                {processMetatags(page.metatag)}
            </SEO>
        </>
    );
};

export const query = graphql`
    query ($id: String!) {
        nodeMarketingMap(id: { eq: $id }) {
            fields {
                url
            }
            title
            drupal_internal__nid
            drupal_internal__vid
            moderation_state
            metatag {
                tag
                attributes {
                    content
                    href
                    name
                    rel
                    property
                }
            }
            field_heading
            body {
                value
            }
        }

        json: file(name: { eq: "markets" }, extension: { eq: "geojson" }) {
            publicURL
        }
    }
`;
